/**
 * C-GTCC-VIEW-HW-GW2 — GTCC view "Gateways" (`gw`), 1:1 after
 * `docs/design/GTCC_REFERENCE_2026-09-01.html`, `div[data-v="gw"]`
 * (lines 772-805), reference frames
 * `docs/design/gtcc-screens/_reference/gw-{dark,light}.png`.
 *
 * Layout of the reference, element for element:
 *   - head "Gateways" with the `.fresh` badge and the `.sub` line of line 774
 *     (its trailing "Beispieldaten." is dropped — this view measures),
 *   - `.grid-main` left column: panel "Gateway-Endpunkte" and panel
 *     "Abgelehnte Verbindungen (Rejections)",
 *   - right column: panel "Ingest /gpsdata (Legacy GT One)" and panel
 *     "Parser-Gesundheit".
 *
 * Sources. Two of the four reads already existed for the old console; only
 * `/v1/admin/gtcc/gw/**` is new, and both of its routes are read-only
 * (`services/api/src/modules/gtcc/gw/`). This ticket adds no write endpoint and
 * no migration:
 *   `GET /v1/admin/gtcc/gw/endpoints`, `GET /v1/admin/gtcc/gw/parser-health`,
 *   `GET /v1/admin/gateway/rejections`, `GET /v1/admin/gateway/ingest`.
 *
 * Nothing here derives an age from the local clock, and no field is wired to a
 * constant "nicht gemessen": every unmeasurable cell is unmeasurable because
 * the payload says so and names its reason (P8/P9/P10).
 *
 * E-GTCC-GW-WRITE-PORT1 — three surfaces of the predecessor
 * `views/gw/GtccGatewayView.tsx` were unreachable after the rebuild bound this
 * component to the registry slot `gw` (`../../registry.ts:437`; regression noted
 * in `docs/design/UNERREICHBARE_UI_FLAECHEN.md` and row 32 of
 * `docs/design/GTCC_COMPLETENESS_REGISTER.md`). They are back:
 *   - the ingest windows 5 min / 1 h / 24 h (only `1h` was drawn),
 *   - the reason filter of the rejection list,
 *   - the card "Unbekannte Geraete", now fed from the payload field
 *     `unknownDevices` of the route instead of the predecessor's client-side
 *     aggregation over the ring buffer.
 * The predecessor issued no mutation, so nothing was ported that writes; the
 * reads and the platform-owner gate are the same ones it used.
 */
import { ErrorState, LoadingState, Stack } from "@genius-tracker/design-system";
import { useLocale } from "@genius-tracker/i18n/react";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import type { ReactNode } from "react";

import {
  GW_ENDPOINTS_KEY,
  GW_INGEST_KEY,
  GW_PARSER_HEALTH_KEY,
  GW_PLATFORM_SURFACE,
  GW_REJECTIONS_KEY,
  GW_REQUIRED_ROLE,
  fetchGwEndpoints,
  fetchGwIngest,
  fetchGwParserHealth,
  fetchGwRejections,
  type GtccResult,
  type GwEndpointsDto,
  type GwIngestDto,
  type GwParserHealthDto,
  type GwRejectionsDto,
} from "../../api/gw.js";
import {
  ControlFreshBadge,
  ControlHead,
  GRID_COLUMN_STYLE,
  GRID_MAIN_CLASS,
  formatInteger,
  measuredNumber,
} from "../control/ControlPrimitives.js";
import {
  GW_QUERY_OPTIONS,
  GwEndpointsPanel,
  GwIngestPanel,
  GwParserHealthPanel,
  GwRejectionsPanel,
  GwUnknownDevicesPanel,
} from "./components/GwPanels.js";

/* --------------------------------------------------------------- helpers */

function dataOf<T>(query: UseQueryResult<GtccResult<T>>): T | null {
  return query.data !== undefined && query.data.ok ? query.data.data : null;
}

/**
 * E-GTCC-GW-WRITE-PORT1 — a refusal is measured per source, not per view.
 *
 * Before this ticket a 403 on one of the four reads left that panel silently
 * empty while the other three drew fine, and only a view in which EVERY source
 * refused said anything at all. Each panel now learns about its own refusal and
 * names the missing role (`GwDeniedNotice`).
 */
function isForbidden(query: { readonly data?: GtccResult<unknown> | undefined }): boolean {
  return query.data !== undefined && !query.data.ok && query.data.kind === "forbidden";
}

/**
 * The badge shows the OLDEST of the freshnesses the payloads carry, because a
 * head that shows the youngest would call the whole view fresh while half of it
 * is stale. Same rule as `HwView.tsx`.
 */
function oldestFreshness(
  sources: ReadonlyArray<{ readonly freshnessSeconds?: number | null } | null>,
): number | null {
  const measured = sources
    .map((source) => measuredNumber(source?.freshnessSeconds))
    .filter((value): value is number => value !== null);
  return measured.length === 0 ? null : Math.max(...measured);
}

/* ------------------------------------------------------------------ view */

export function GwView(): ReactNode {
  const { t, locale } = useLocale();

  const endpointsQuery = useQuery({
    queryKey: GW_ENDPOINTS_KEY,
    queryFn: ({ signal }) => fetchGwEndpoints(signal),
    ...GW_QUERY_OPTIONS,
  });
  const parserQuery = useQuery({
    queryKey: GW_PARSER_HEALTH_KEY,
    queryFn: ({ signal }) => fetchGwParserHealth(signal),
    ...GW_QUERY_OPTIONS,
  });
  const rejectionsQuery = useQuery({
    queryKey: GW_REJECTIONS_KEY,
    queryFn: ({ signal }) => fetchGwRejections(signal),
    ...GW_QUERY_OPTIONS,
  });
  const ingestQuery = useQuery({
    queryKey: GW_INGEST_KEY,
    queryFn: ({ signal }) => fetchGwIngest(signal),
    ...GW_QUERY_OPTIONS,
  });

  const endpoints: GwEndpointsDto | null = dataOf(endpointsQuery);
  const parser: GwParserHealthDto | null = dataOf(parserQuery);
  const rejections: GwRejectionsDto | null = dataOf(rejectionsQuery);
  const ingest: GwIngestDto | null = dataOf(ingestQuery);

  const queries = [endpointsQuery, parserQuery, rejectionsQuery, ingestQuery];
  /* Nur `isPending`: eine mit einem Fehler gesettelte Query traegt kein `data`
     — wer sie als „laedt noch" zaehlt, dreht den Spinner ewig. */
  const allPending = queries.every((query) => query.isPending);
  const anyLoaded = endpoints !== null || parser !== null || rejections !== null || ingest !== null;
  const deniedOnly = queries.some((query) => isForbidden(query));
  const ingestDenied = isForbidden(ingestQuery);
  const rejectionsDenied = isForbidden(rejectionsQuery);

  const freshSeconds = oldestFreshness([endpoints, parser]);

  const head = (
    <ControlHead
      testId="gtcc-gw-head"
      title={t("admin.gtcc.gw.head.title")}
      subtitle={t("admin.gtcc.gw.head.subtitle")}
      fresh={
        <ControlFreshBadge
          testId="gtcc-gw-fresh"
          text={
            freshSeconds === null
              ? t("admin.gtcc.gw.fresh.unmeasurable")
              : t("admin.gtcc.gw.fresh.seconds", {
                  seconds: formatInteger(freshSeconds, locale),
                })
          }
        />
      }
    />
  );

  if (allPending) {
    return (
      <Stack gap={3} data-testid="gtcc-view-gw">
        {head}
        <div data-testid="gtcc-gw-loading">
          <LoadingState label={t("admin.gtcc.gw.loading")} />
        </div>
      </Stack>
    );
  }

  if (!anyLoaded) {
    return (
      <Stack gap={3} data-testid="gtcc-view-gw">
        {head}
        <div data-testid="gtcc-gw-failure">
          <ErrorState
            severity="warn"
            title={t(deniedOnly ? "admin.gtcc.gw.forbidden.title" : "admin.gtcc.gw.failure.title")}
            /* Auch hier wird die fehlende Berechtigung genannt, nicht nur die
               Verweigerung behauptet (E-GTCC-GW-WRITE-PORT1). */
            body={
              deniedOnly
                ? t("admin.gtcc.gw.forbidden.body", {
                    role: GW_REQUIRED_ROLE,
                    surface: GW_PLATFORM_SURFACE,
                  })
                : t("admin.gtcc.gw.failure.body")
            }
          />
        </div>
      </Stack>
    );
  }

  return (
    <Stack gap={3} data-testid="gtcc-view-gw">
      {head}
      <div className={GRID_MAIN_CLASS}>
        <div style={GRID_COLUMN_STYLE}>
          <GwEndpointsPanel endpoints={endpoints} locale={locale} />
          <GwRejectionsPanel rejections={rejections} locale={locale} denied={rejectionsDenied} />
        </div>
        <div style={GRID_COLUMN_STYLE}>
          <GwIngestPanel ingest={ingest} locale={locale} denied={ingestDenied} />
          <GwParserHealthPanel health={parser} />
          <GwUnknownDevicesPanel ingest={ingest} locale={locale} denied={ingestDenied} />
        </div>
      </div>
    </Stack>
  );
}

export default GwView;
